import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail } from 'lucide-react';
import { Card, Button } from '../components';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

type Status = 'loading' | 'success' | 'error';

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { isAuthenticated, user, updateUser } = useAuthStore();
  const isEmployer = user?.role === 'opdrachtgever' || user?.role === 'employer_member';

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Geen verificatietoken gevonden in de link.');
      return;
    }

    authAPI.verifyEmail(token)
      .then(() => {
        setStatus('success');
        updateUser({ is_verified: true });
      })
      .catch((err: any) => {
        setStatus('error');
        setErrorMessage(err.response?.data?.message || 'Verificatielink is ongeldig of verlopen.');
      });
  }, [token, updateUser]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResending(true);
    try {
      await authAPI.resendVerification(resendEmail);
      setResendSent(true);
      toast.success('Als dit e-mailadres bestaat, is er een nieuwe verificatielink verstuurd.');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Versturen mislukt. Probeer het later opnieuw.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full">
        <Card>
          <div className="p-6 text-center">
            {status === 'loading' && (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <h1 className="text-xl font-bold text-gray-900">E-mailadres verifiëren...</h1>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">E-mailadres geverifieerd</h1>
                <p className="text-gray-600 mb-6">
                  {isEmployer
                    ? 'Uw e-mailadres is bevestigd. U kunt nu verder met de onboarding: KvK-verificatie.'
                    : 'Je e-mailadres is bevestigd. Je kunt nu verder met stap 2: KVK-koppeling.'}
                </p>
                {isAuthenticated ? (
                  <Button
                    onClick={() => navigate(isEmployer ? '/employer/onboarding' : '/professional/screening')}
                    className="w-full"
                  >
                    {isEmployer ? 'Verder met de onboarding' : 'Verder naar KVK-koppeling'}
                  </Button>
                ) : (
                  <Button onClick={() => navigate('/login')} className="w-full">
                    Inloggen
                  </Button>
                )}
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">Verificatie mislukt</h1>
                <p className="text-gray-600 mb-6">{errorMessage}</p>

                {!resendSent ? (
                  <form onSubmit={handleResend} className="text-left space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Vraag een nieuwe verificatielink aan
                    </label>
                    <div className="flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <input
                        type="email"
                        required
                        placeholder="jouw@email.nl"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className="flex-1 outline-none text-sm"
                      />
                    </div>
                    <Button type="submit" loading={resending} className="w-full">
                      Nieuwe link versturen
                    </Button>
                  </form>
                ) : (
                  <p className="text-sm text-green-700">
                    Controleer je inbox voor de nieuwe verificatielink.
                  </p>
                )}

                <p className="text-sm text-gray-500 mt-6">
                  <Link to="/login" className="text-blue-600 hover:underline">Terug naar inloggen</Link>
                </p>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
